/**
 * Détection et parsing de CSV SuiviFinance + relevés bancaires FR.
 * Module pur (pas de React Native) — réutilisable web & mobile.
 */

export type BankId =
  | 'boursobank'
  | 'credit_agricole'
  | 'bnp'
  | 'lcl'
  | 'sg'
  | 'fortuneo'
  | 'sumeria'
  | 'suivifinance'
  | 'unknown';

export type ParsedRowType = 'depense' | 'revenu' | 'transfert';

export interface ParsedRow {
  date: string; // YYYY-MM-DD
  description: string;
  /** Négatif = dépense / sortie ; positif = revenu */
  montant: number;
  type: ParsedRowType;
  categorieHint?: string;
  compteHint?: string;
  compteDestinationHint?: string;
  rawLine?: number;
  warning?: string;
}

export interface ParsedImport {
  bank: BankId;
  bankLabel: string;
  rows: ParsedRow[];
  errors: string[];
  separator: ',' | ';';
  encoding: string;
  headerLineIndex: number;
}

const BANK_LABELS: Record<BankId, string> = {
  boursobank: 'BoursoBank',
  credit_agricole: 'Crédit Agricole',
  bnp: 'BNP Paribas',
  lcl: 'LCL',
  sg: 'Société Générale',
  fortuneo: 'Fortuneo',
  sumeria: 'Sumeria',
  suivifinance: 'SuiviFinance',
  unknown: 'Format inconnu',
};

const CATEGORY_RULES: Array<{ pattern: RegExp; category: string }> = [
  { pattern: /\b(LIDL|ALDI|LECLERC|CARREFOUR|AUCHAN|INTERMARCHE|INTERMARCHÉ|CASINO|MONOPRIX|FRANPRIX)\b/i, category: 'Courses' },
  { pattern: /\b(TOTAL|SHELL|BP |ESSO|INTERMARCHE FUEL|STATION)\b/i, category: 'Gazoil' },
  { pattern: /\b(EDF|ENGIE|ENEDIS)\b/i, category: 'Électricité' },
  { pattern: /\bGRDF\b/i, category: 'Gaz' },
  { pattern: /\b(FREE|SFR|ORANGE|BOUYGUES)\b/i, category: 'Téléphone et Internet' },
  { pattern: /\b(AMAZON|FNAC|DARTY|LDLC|MATERIEL\.NET)\b/i, category: 'Informatique' },
  { pattern: /\b(SNCF|RATP|UBER|BOLT|BLAABLACAR|FLIXBUS)\b/i, category: 'Transport' },
  { pattern: /\b(MCDO|MCDONALD|KFC|BURGER KING|QUICK|SUBWAY)\b/i, category: 'Restaurant' },
  { pattern: /\b(SALAIRE|VIREMENT EMPLOYEUR|PAIE )\b/i, category: 'Salaire' },
];

export function suggestCategory(libelle: string): string | undefined {
  const text = libelle || '';
  for (const rule of CATEGORY_RULES) {
    if (rule.pattern.test(text)) return rule.category;
  }
  return undefined;
}

export function stripBom(content: string): string {
  return content.replace(/^\uFEFF/, '');
}

/**
 * Les exports Fortuneo (et souvent CA/LCL) sont en Windows-1252.
 * FileReader UTF-8 produit � / mojibake — on retombe sur latin1.
 */
export function decodeCsvBytes(bytes: ArrayBuffer | Uint8Array): string {
  const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  const utf8 = new TextDecoder('utf-8', { fatal: false }).decode(u8);
  const bad =
    utf8.includes('\uFFFD') ||
    /Date opÃ|libellÃ|DÃ©bit|CrÃ©dit|opÃ©ration/i.test(utf8) ||
    // UTF-8 mal décodé depuis CP1252 : "Date op" + char 0xE9 seul → souvent �
    /Date op\uFFFD|libell\uFFFD|D\uFFFDbit|Cr\uFFFDdit/i.test(utf8);
  if (!bad) return stripBom(utf8);
  try {
    return stripBom(new TextDecoder('windows-1252').decode(u8));
  } catch {
    // Fallback navigateur ancien
    let s = '';
    for (let i = 0; i < u8.length; i++) s += String.fromCharCode(u8[i]);
    return stripBom(s);
  }
}

/** Heuristique sur une string déjà lue : si mojibake, tenter latin1 via char codes. */
export function maybeFixLatin1Mojibake(text: string): string {
  const t = stripBom(text || '');
  if (!t.includes('\uFFFD') && !/Ã[©©¨ª«]/.test(t)) return t;
  // Si on n'a que la string (pas les bytes), on ne peut pas toujours réparer ;
  // les en-têtes Fortuneo restent matchables via regex souples (d.?bit).
  return t;
}

export function detectSeparator(sample: string): ',' | ';' {
  const lines = sample.split(/\r?\n/).slice(0, 15);
  let semi = 0;
  let comma = 0;
  for (const line of lines) {
    if (!line.trim()) continue;
    semi += (line.match(/;/g) || []).length;
    comma += (line.match(/,/g) || []).length;
  }
  return semi >= comma ? ';' : ',';
}

/** Parse une ligne CSV en respectant les guillemets. */
export function parseDelimitedLine(line: string, separator: ',' | ';'): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      if (line[i + 1] === '"' && inQuotes) {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === separator && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current.trim());
  return result;
}

export function parseFrenchAmount(raw: string | undefined | null): number | null {
  if (raw == null) return null;
  let s = String(raw).trim();
  if (!s || s === '-' || s === '—') return null;
  // Enlever espaces / NBSP / symboles monétaires
  s = s.replace(/\s|\u00A0|€|EUR/gi, '');
  // 1.234,56 → 1234.56 ; 1234,56 → 1234.56 ; -45,60
  if (s.includes(',') && s.includes('.')) {
    s = s.replace(/\./g, '').replace(',', '.');
  } else {
    s = s.replace(',', '.');
  }
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

export function parseFlexibleDate(dateStr: string): string | null {
  const raw = (dateStr || '').trim();
  if (!raw) return null;
  try {
    // ISO datetime Sumeria : 2026-10-24T10:36:25+00:00
    const isoDay = raw.match(/^(\d{4}-\d{2}-\d{2})/);
    if (isoDay) {
      const [y, m, d] = isoDay[1].split('-').map((x) => parseInt(x, 10));
      const dt = new Date(y, m - 1, d);
      if (
        !isNaN(dt.getTime()) &&
        dt.getFullYear() === y &&
        dt.getMonth() === m - 1 &&
        dt.getDate() === d
      ) {
        return isoDay[1];
      }
    }

    const parts = raw.split(/[-/]/);
    if (parts.length !== 3) return null;
    let year: number;
    let month: number;
    let day: number;
    if (raw.includes('/')) {
      day = parseInt(parts[0], 10);
      month = parseInt(parts[1], 10);
      year = parseInt(parts[2], 10);
      if (year < 100) year += 2000;
    } else if (parts[0].length === 4) {
      year = parseInt(parts[0], 10);
      month = parseInt(parts[1], 10);
      day = parseInt(parts[2], 10);
    } else {
      day = parseInt(parts[0], 10);
      month = parseInt(parts[1], 10);
      year = parseInt(parts[2], 10);
    }
    const d = new Date(year, month - 1, day);
    if (isNaN(d.getTime())) return null;
    if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return null;
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  } catch {
    return null;
  }
}

function normalizeHeader(h: string): string {
  return h
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function findCol(headers: string[], predicates: Array<(h: string) => boolean>): number {
  for (let i = 0; i < headers.length; i++) {
    const h = normalizeHeader(headers[i]);
    if (predicates.some((p) => p(h))) return i;
  }
  return -1;
}

export function detectBank(csvContent: string, forced?: BankId | 'auto'): BankId {
  if (forced && forced !== 'auto' && forced !== 'unknown') return forced;
  const text = stripBom(csvContent);
  const head = text.slice(0, 4000);
  const lower = head.toLowerCase();

  // BoursoBank export web : Date Opération + Libellé Suggéré / Catégorie Parente + Solde
  if (
    /dateop/i.test(head) ||
    /boursorama/i.test(head) ||
    /boursobank/i.test(head) ||
    (/libell[ée]?\s*sugg/i.test(head) && /cat[ée]gorie\s*parente/i.test(head)) ||
    (/date\s*op/i.test(head) && /date\s*valeur/i.test(head) && /;\s*solde\s*;/i.test(head))
  ) {
    return 'boursobank';
  }
  // Sumeria (ex-Lydia) : préambule + "Date de l'opération,Titre,Montant"
  if (
    /sumeria/i.test(lower) ||
    (/date de l['’]?op/i.test(head) && /\btitre\b/i.test(head) && /\bmontant\b/i.test(head)) ||
    (/filtres d['’]?inclusion/i.test(lower) && /\btitre\b/i.test(head))
  ) {
    return 'sumeria';
  }
  // Fortuneo : "Date opération;Date valeur;libellé;Débit;Crédit;"
  // (souvent CP1252 — accents peuvent être �)
  if (
    (/date\s*op/i.test(head) || /historiqueoperations/i.test(lower)) &&
    /date\s*valeur/i.test(head) &&
    /libell/i.test(head) &&
    /d.bit/i.test(head) &&
    /cr.dit/i.test(head) &&
    !/d[ée]bit\s*euros/i.test(head) &&
    !/;\s*solde\s*;/i.test(head)
  ) {
    return 'fortuneo';
  }
  if (/date valeur/i.test(head) && /d[ée]bit euros/i.test(head)) {
    return 'credit_agricole';
  }
  if (
    /r[ée]f[ée]rence/i.test(head) &&
    /d[ée]bit/i.test(head) &&
    /cr[ée]dit/i.test(head)
  ) {
    return 'sg';
  }
  if (
    (/libell[ée]/i.test(head) || /libelle/i.test(lower)) &&
    /montant/i.test(head) &&
    /devise/i.test(head)
  ) {
    return 'bnp';
  }
  if (
    /d[ée]bit/i.test(head) &&
    /cr[ée]dit/i.test(head) &&
    !/date valeur/i.test(head) &&
    !/r[ée]f[ée]rence/i.test(head)
  ) {
    // Could be LCL-like
    if (/^date;/im.test(head) || /;libell/i.test(head)) return 'lcl';
  }
  if (
    /description/i.test(head) &&
    /type/i.test(head) &&
    (/cat[ée]gorie/i.test(head) || /categorie/i.test(lower))
  ) {
    return 'suivifinance';
  }
  return 'unknown';
}

function findHeaderLineIndex(lines: string[], bank: BankId, separator: ',' | ';'): number {
  for (let i = 0; i < Math.min(lines.length, 40); i++) {
    const line = lines[i];
    if (!line.trim()) continue;
    const lower = line.toLowerCase();
    switch (bank) {
      case 'boursobank':
        if (
          /dateop/i.test(line) ||
          (/date\s*op/i.test(line) && /libell/i.test(line) && (/solde/i.test(line) || /cat[ée]gorie/i.test(line)))
        ) {
          return i;
        }
        break;
      case 'sumeria':
        if (/date de l['’]?op/i.test(line) && /titre/i.test(line) && /montant/i.test(line)) return i;
        break;
      case 'credit_agricole':
        if (/^date;/i.test(line) && /date valeur/i.test(line)) return i;
        break;
      case 'fortuneo':
        if (/date\s*op/i.test(line) && /libell/i.test(line) && /d.bit/i.test(line)) return i;
        break;
      case 'bnp':
        if (/montant/i.test(lower) && /devise/i.test(lower)) return i;
        break;
      case 'sg':
        if (/r[ée]f[ée]rence/i.test(line) && /d[ée]bit/i.test(line)) return i;
        break;
      case 'lcl':
        if (/^date;/i.test(line) && /d[ée]bit/i.test(line) && /cr[ée]dit/i.test(line)) return i;
        break;
      case 'suivifinance':
        if (/description/i.test(lower) && /montant/i.test(lower)) return i;
        break;
      default:
        if (parseDelimitedLine(line, separator).length >= 3) {
          if (/date/i.test(lower) && (/libell/i.test(lower) || /description/i.test(lower) || /montant/i.test(lower))) {
            return i;
          }
        }
    }
  }
  return 0;
}

function rowFromDebitCredit(
  date: string,
  libelle: string,
  debitRaw: string | undefined,
  creditRaw: string | undefined,
  lineNo: number,
): ParsedRow | { error: string } {
  const debit = parseFrenchAmount(debitRaw);
  const credit = parseFrenchAmount(creditRaw);
  let montant = 0;
  let type: ParsedRowType = 'depense';
  if (debit != null && Math.abs(debit) > 0) {
    montant = -Math.abs(debit);
    type = 'depense';
  } else if (credit != null && Math.abs(credit) > 0) {
    montant = Math.abs(credit);
    type = 'revenu';
  } else {
    return { error: `Ligne ${lineNo}: ni débit ni crédit` };
  }
  return {
    date,
    description: libelle || 'Sans libellé',
    montant,
    type,
    categorieHint: suggestCategory(libelle),
    rawLine: lineNo,
  };
}

function parseBankTable(
  lines: string[],
  headerIdx: number,
  separator: ',' | ';',
  bank: BankId,
): { rows: ParsedRow[]; errors: string[] } {
  const headers = parseDelimitedLine(lines[headerIdx], separator).map(normalizeHeader);
  const errors: string[] = [];
  const rows: ParsedRow[] = [];

  const dateIdx = findCol(headers, [
    (h) =>
      h === 'date' ||
      h === 'dateop' ||
      h.startsWith('date op') ||
      h === 'date operation' ||
      /^date\s*op/.test(h) ||
      /^date de l['']?op/.test(h),
  ]);
  const libelleIdx = findCol(headers, [
    (h) =>
      h === 'titre' ||
      h === 'description' ||
      h === 'libelle' ||
      (h.includes('libell') && !h.includes('sugg') && !h.includes('compte')),
  ]);
  // Souple pour CP1252 mal décodé (d�bit / cr�dit)
  const debitIdx = findCol(headers, [
    (h) => /d.?bit/.test(h) && !h.includes('date'),
  ]);
  const creditIdx = findCol(headers, [
    (h) => /cr.?dit/.test(h),
  ]);
  // BoursoBank export : montant signé dans la 1re colonne "Solde"
  const montantIdx = findCol(headers, [
    (h) =>
      h === 'montant' ||
      h.startsWith('montant') ||
      (bank === 'boursobank' && h === 'solde'),
  ]);
  const typeIdx = findCol(headers, [(h) => h === 'type']);
  const catIdx = findCol(headers, [
    (h) => h === 'categorie' || (h.includes('categorie') && !h.includes('parente')),
  ]);
  const compteIdx = findCol(headers, [
    (h) => h === 'libelle compte',
    (h) => h === 'compte',
    (h) => h.endsWith(' compte') && !h.includes('numero') && !h.includes('num'),
  ]);
  const destIdx = findCol(headers, [(h) => h.includes('destination')]);

  if (dateIdx < 0) {
    errors.push('Colonne date introuvable');
    return { rows, errors };
  }

  for (let i = headerIdx + 1; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim()) continue;
    const cols = parseDelimitedLine(line, separator);
    if (cols.every((c) => !c)) continue;

    const date = parseFlexibleDate(cols[dateIdx] || '');
    if (!date) {
      errors.push(`Ligne ${i + 1}: date invalide (${cols[dateIdx] || ''})`);
      continue;
    }
    const libelle = (libelleIdx >= 0 ? cols[libelleIdx] : '') || '';

    // SuiviFinance format
    if (bank === 'suivifinance' || (montantIdx >= 0 && typeIdx >= 0)) {
      const rawMontant = parseFrenchAmount(cols[montantIdx]);
      if (rawMontant == null || rawMontant === 0) {
        errors.push(`Ligne ${i + 1}: montant invalide`);
        continue;
      }
      const typeStr = (typeIdx >= 0 ? cols[typeIdx] : 'depense').toLowerCase();
      let type: ParsedRowType = 'depense';
      if (['revenu', 'revenus', 'recette', 'recettes'].includes(typeStr)) type = 'revenu';
      else if (['transfert', 'virement'].includes(typeStr)) type = 'transfert';
      const abs = Math.abs(rawMontant);
      const montant =
        type === 'revenu' ? abs : type === 'transfert' ? -abs : -abs;
      rows.push({
        date,
        description: libelle || (cols[findCol(headers, [(h) => h.includes('description')])] || 'Sans description'),
        montant,
        type,
        categorieHint: (catIdx >= 0 ? cols[catIdx] : undefined) || suggestCategory(libelle),
        compteHint: compteIdx >= 0 ? cols[compteIdx] : undefined,
        compteDestinationHint: destIdx >= 0 ? cols[destIdx] : undefined,
        rawLine: i + 1,
      });
      continue;
    }

    // Montant signé : BNP, BoursoBank (Solde), Sumeria (Montant)
    if (
      bank === 'bnp' ||
      bank === 'boursobank' ||
      bank === 'sumeria' ||
      (montantIdx >= 0 && debitIdx < 0 && creditIdx < 0)
    ) {
      if (montantIdx < 0) {
        errors.push(`Ligne ${i + 1}: colonne montant introuvable`);
        continue;
      }
      const m = parseFrenchAmount(cols[montantIdx]);
      if (m == null || m === 0) {
        errors.push(`Ligne ${i + 1}: montant invalide`);
        continue;
      }
      const catHint =
        (catIdx >= 0 ? cols[catIdx] : undefined) || suggestCategory(libelle);
      rows.push({
        date,
        description: libelle || 'Sans libellé',
        montant: m,
        type: m < 0 ? 'depense' : 'revenu',
        categorieHint: catHint,
        compteHint: compteIdx >= 0 ? cols[compteIdx] : undefined,
        rawLine: i + 1,
      });
      continue;
    }

    // Debit/Credit columns
    const parsed = rowFromDebitCredit(
      date,
      libelle,
      debitIdx >= 0 ? cols[debitIdx] : undefined,
      creditIdx >= 0 ? cols[creditIdx] : undefined,
      i + 1,
    );
    if ('error' in parsed) {
      errors.push(parsed.error);
      continue;
    }
    rows.push(parsed);
  }

  return { rows, errors };
}

export function detectAndParse(
  csvContent: string,
  options?: { forcedBank?: BankId | 'auto' },
): ParsedImport {
  const encodingNotes: string[] = [];
  const content = stripBom(csvContent || '');
  if (/[ÃÂâã]/.test(content)) {
    encodingNotes.push(
      'Caractères accentués potentiellement corrompus — réexportez le CSV en UTF-8 si possible.',
    );
  }

  const bank = detectBank(content, options?.forcedBank);
  // SuiviFinance often comma; banks usually semicolon — auto-detect if unknown
  const sep: ',' | ';' =
    bank === 'unknown' || bank === 'suivifinance' || bank === 'sumeria'
      ? detectSeparator(content.slice(0, 3000))
      : ';';

  const lines = content.split(/\r?\n/);
  const headerLineIndex = findHeaderLineIndex(lines, bank, sep);
  const { rows, errors } = parseBankTable(lines, headerLineIndex, sep, bank);

  return {
    bank,
    bankLabel: BANK_LABELS[bank],
    rows,
    errors: [...encodingNotes, ...errors],
    separator: sep,
    encoding: 'UTF-8',
    headerLineIndex,
  };
}

export { BANK_LABELS };
